import { memo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "shared/components/ui/table";
import HeaderIndicator from "./HeaderIndicator";

import styles from "../ComposizioneDelPortafoglio.module.scss";
import { ProductData, PortfolioTableProps } from "../types";
import { updateGlobalTooltipText } from "../utils";

// Memoized complete table component
const ComposizioneDelPortafoglioTable = memo(
  ({
    data,
    headers,
    sortConfig,
    columnMappings,
    columnWidths,
    colorsConfig,
    handleSort,
    onTooltipTrigger,
    onTooltipHide,
  }: PortfolioTableProps) => {
    // Table styles
    const tableStyles = {
      customTable: {
        borderCollapse: "collapse" as const,
        width: "100%",
      },
    };

    const formatCellValue = useCallback(
      (key: string, product: ProductData): string => {
        if (key === "peso_sul_ptf") {
          return Number(product[key]).toFixed(2);
        }
        if (key === "final_score_advice") {
          return product["final_score"] + "/" + product[key];
        }
        return String(product[key]);
      },
      [],
    );

    const handleCellClick = useCallback((text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          updateGlobalTooltipText("Copied");
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }, []);

    return (
      <>
        <style>
          {`
          .customTable {
            table-layout: fixed;
          }
          .customTable th {
            border-bottom: none !important;
            padding: 8px 16px;
            position: relative;
          }
          
          .customTable tbody tr:first-child td {
            border-top: none !important;
            padding-top: 8px !important;
          }
        `}
        </style>
        <Table
          className={`${styles.table} customTable`}
          style={tableStyles.customTable}
        >
          <TableHeader>
            <TableRow>
              {headers.map((header, index) => {
                let textAlignment = "center";
                if (
                  header === "rating_ESG" ||
                  header === "costi_totali_stimati"
                ) {
                  textAlignment = "right";
                }

                const isSpecialColumn = index >= headers.length - 5;
                const specialClass = isSpecialColumn ? "specialBorder" : "";
                return (
                  <TableHead
                    key={index}
                    className={specialClass}
                    onClick={isSpecialColumn ? () => handleSort(header) : null}
                    style={{
                      fontWeight: "bold",
                      // width: `${columnWidths[index]}%`,
                      width: `${columnWidths[index]}px`,
                      minWidth: `${columnWidths[index]}px`,
                      maxWidth: `${columnWidths[index]}px`,
                      textAlign: textAlignment as any,
                      cursor: isSpecialColumn ? "pointer" : "default",
                    }}
                  >
                    {columnMappings[header] || header}
                    {isSpecialColumn && (
                      <HeaderIndicator color={colorsConfig.FinecoBluePrimary} />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((product, index) => (
              <TableRow key={index}>
                {headers.map((key, cellIndex) => {
                  let textAlignment = "center";
                  if (key === "rating_ESG" || key === "costi_totali_stimati") {
                    textAlignment = "right";
                  }

                  const valueToRender = formatCellValue(key, product);

                  return (
                    <TableCell
                      key={cellIndex}
                      onMouseEnter={(e) =>
                        onTooltipTrigger(e, String(product[key]))
                      }
                      onMouseLeave={onTooltipHide}
                      onClick={() => handleCellClick(String(product[key]))}
                      style={{
                        // width: `${columnWidths[cellIndex]}%`,
                        width: `${columnWidths[index]}px`,
                        minWidth: `${columnWidths[index]}px`,
                        maxWidth: `${columnWidths[index]}px`,
                        textAlign: textAlignment as any,
                      }}
                    >
                      {valueToRender}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if data or sort configuration changes
    return (
      prevProps.data === nextProps.data &&
      prevProps.sortConfig.key === nextProps.sortConfig.key &&
      prevProps.sortConfig.direction === nextProps.sortConfig.direction
    );
  },
);

export default ComposizioneDelPortafoglioTable;
